/**
 * Opening catalog — data only.
 * FENs are built from mainline SANs via chess.js at module load.
 * Grouped by White’s first move: e4 | d4 | other.
 */
import { Chess } from 'chess.js';

export type OpeningSide = 'white' | 'black';

/** First-move bucket for grouping lists. */
export type OpeningGroupId = 'e4' | 'd4' | 'other';

export interface Opening {
  name: string;
  moves: string;
  side: OpeningSide;
  fen: string;
  /** Derived from the first SAN in `moves`. */
  group: OpeningGroupId;
}

export interface OpeningGroup {
  id: OpeningGroupId;
  label: string;
  openings: Opening[];
}

export const OPENING_GROUP_ORDER: OpeningGroupId[] = ['e4', 'd4', 'other'];

export const OPENING_GROUP_LABELS: Record<OpeningGroupId, string> = {
  e4: 'E4 (King’s Pawn First)',
  d4: 'D4 (Queen’s Pawn First)',
  other: 'Other',
};

function fenFromMoves(moves: string): string {
  const game = new Chess();
  const parts = moves.trim().split(/\s+/).filter(Boolean);
  for (const san of parts) {
    const result = game.move(san);
    if (!result) {
      throw new Error(`Invalid SAN in opening line: "${san}" (moves: ${moves})`);
    }
  }
  return game.fen();
}

export function groupFromMoves(moves: string): OpeningGroupId {
  const first = moves.trim().split(/\s+/)[0] ?? '';
  if (first === 'e4') return 'e4';
  if (first === 'd4') return 'd4';
  return 'other';
}

function define(side: OpeningSide, name: string, moves: string): Opening {
  return {
    side,
    name,
    moves,
    fen: fenFromMoves(moves),
    group: groupFromMoves(moves),
  };
}

const WHITE_RAW: Array<[string, string]> = [
  // 1. e4
  ['Alapin Sicilian', 'e4 c5 c3'],
  ['Austrian Attack', 'e4 d6 d4 Nf6 Nc3 g6 f4'],
  ['Bishop’s Gambit', 'e4 e5 f4 exf4 Bc4'],
  ['Bishop’s Opening', 'e4 e5 Bc4'],
  ['Caro-Kann Advance', 'e4 c6 d4 d5 e5'],
  ['Caro-Kann Two Knights', 'e4 c6 Nc3 d5 Nf3'],
  ['Center Game', 'e4 e5 d4'],
  ['Closed Sicilian', 'e4 c5 Nc3'],
  ['Danish Gambit', 'e4 e5 d4 exd4 c3'],
  ['Evans Gambit', 'e4 e5 Nf3 Nc6 Bc4 Bc5 b4'],
  ['Four Knights Game', 'e4 e5 Nf3 Nc6 Nc3 Nf6'],
  ['French Advance', 'e4 e6 d4 d5 e5'],
  ['French Exchange', 'e4 e6 d4 d5 exd5'],
  ['French Tarrasch', 'e4 e6 d4 d5 Nd2'],
  ['Fried Liver Attack', 'e4 e5 Nf3 Nc6 Bc4 Nf6 Ng5 d5 exd5 Nxd5 Nxf7'],
  ['Giuoco Piano', 'e4 e5 Nf3 Nc6 Bc4 Bc5'],
  ['Grand Prix Attack', 'e4 c5 Nc3 Nc6 f4'],
  ['Göring Gambit', 'e4 e5 Nf3 Nc6 d4 exd4 c3'],
  ['Halloween Gambit', 'e4 e5 Nf3 Nc6 Nc3 Nf6 Nxe5'],
  ['Italian Gambit', 'e4 e5 Nf3 Nc6 Bc4 Bc5 d4'],
  ['Italian Game', 'e4 e5 Nf3 Nc6 Bc4'],
  ['King’s Gambit', 'e4 e5 f4'],
  ['King’s Gambit Accepted', 'e4 e5 f4 exf4'],
  ['Moscow Variation', 'e4 c5 Nf3 d6 Bb5+'],
  ['Panov Attack', 'e4 c6 d4 d5 exd5 cxd5 c4'],
  ['Ponziani Opening', 'e4 e5 Nf3 Nc6 c3'],
  ['Rossolimo Attack', 'e4 c5 Nf3 Nc6 Bb5'],
  ['Ruy Lopez', 'e4 e5 Nf3 Nc6 Bb5'],
  ['Scotch Gambit', 'e4 e5 Nf3 Nc6 d4 exd4 Bc4'],
  ['Scotch Game', 'e4 e5 Nf3 Nc6 d4'],
  ['Smith-Morra Gambit', 'e4 c5 d4 cxd4 c3'],
  ['Urusov Gambit', 'e4 e5 Bc4 Nf6 d4'],
  ['Vienna Gambit', 'e4 e5 Nc3 Nf6 f4'],
  ['Vienna Game', 'e4 e5 Nc3'],
  ['Wing Gambit', 'e4 c5 b4'],
  // 1. d4
  ['Barry Attack', 'd4 Nf6 Nf3 g6 Nc3 d5 Bf4'],
  ['Blackmar-Diemer Gambit', 'd4 d5 e4'],
  ['Catalan Opening', 'd4 Nf6 c4 e6 g3'],
  ['Colle System', 'd4 d5 Nf3 Nf6 e3'],
  ['Four Pawns Attack', 'd4 Nf6 c4 g6 Nc3 Bg7 e4 d6 f4'],
  ['Hodgson Attack', 'd4 d5 Bg5'],
  ['Jobava London', 'd4 Nf6 Nc3 d5 Bf4'],
  ['London System', 'd4 d5 Nf3 Nf6 Bf4'],
  ['Queen’s Gambit', 'd4 d5 c4'],
  ['Queen’s Gambit Exchange', 'd4 d5 c4 e6 Nc3 Nf6 cxd5'],
  ['Queen’s Pawn Opening', 'd4'],
  ['Richter-Veresov Attack', 'd4 d5 Nc3 Nf6 Bg5'],
  ['Sämisch Variation', 'd4 Nf6 c4 g6 Nc3 Bg7 e4 d6 f3'],
  ['Stonewall Attack', 'd4 d5 e3 Nf6 Bd3 c5 c3'],
  ['Torre Attack', 'd4 Nf6 Nf3 e6 Bg5'],
  ['Trompowsky Attack', 'd4 Nf6 Bg5'],
  // Other
  ['Anderssen’s Opening', 'a3'],
  ['Bird’s Opening', 'f4'],
  ['English Four Knights', 'c4 e5 Nc3 Nf6 Nf3 Nc6'],
  ['English Opening', 'c4'],
  ['Grob Opening', 'g4'],
  ['King’s Indian Attack', 'Nf3 d5 g3'],
  ['Larsen’s Opening', 'b3'],
  ['Réti Opening', 'Nf3'],
  ['Sokolsky Opening', 'b4'],
  ['Symmetrical English', 'c4 c5'],
  ['Van Geet Opening', 'Nc3'],
  ['Van’t Kruijs Opening', 'e3'],
];

const BLACK_RAW: Array<[string, string]> = [
  // vs 1. e4
  ['Accelerated Dragon', 'e4 c5 Nf3 Nc6 d4 cxd4 Nxd4 g6'],
  ['Alekhine Defense', 'e4 Nf6'],
  ['Berlin Defense', 'e4 e5 Nf3 Nc6 Bb5 Nf6'],
  ['Caro-Kann Classical', 'e4 c6 d4 d5 Nc3 dxe4 Nxe4 Bf5'],
  ['Caro-Kann Defense', 'e4 c6'],
  ['Caro-Kann Steinitz', 'e4 c6 d4 d5 Nc3 dxe4 Nxe4 Nd7'],
  ['Classical Sicilian', 'e4 c5 Nf3 d6 d4 cxd4 Nxd4 Nf6 Nc3 Nc6'],
  ['Elephant Gambit', 'e4 e5 Nf3 d5'],
  ['French Classical', 'e4 e6 d4 d5 Nc3 Nf6'],
  ['French Defense', 'e4 e6'],
  ['French Rubinstein', 'e4 e6 d4 d5 Nc3 dxe4'],
  ['French Winawer', 'e4 e6 d4 d5 Nc3 Bb4'],
  ['Hungarian Defense', 'e4 e5 Nf3 Nc6 Bc4 Be7'],
  ['Kalashnikov Variation', 'e4 c5 Nf3 Nc6 d4 cxd4 Nxd4 e5'],
  ['Kan Variation', 'e4 c5 Nf3 e6 d4 cxd4 Nxd4 a6'],
  ['Latvian Gambit', 'e4 e5 Nf3 f5'],
  ['Modern Defense', 'e4 g6'],
  ['Morphy Defense', 'e4 e5 Nf3 Nc6 Bb5 a6'],
  ['Najdorf Variation', 'e4 c5 Nf3 d6 d4 cxd4 Nxd4 Nf6 Nc3 a6'],
  ['Nimzowitsch Defense', 'e4 Nc6'],
  ['Owen’s Defense', 'e4 b6'],
  ['Petrov Defense', 'e4 e5 Nf3 Nf6'],
  ['Philidor Defense', 'e4 e5 Nf3 d6'],
  ['Pirc Defense', 'e4 d6 d4 Nf6 Nc3 g6'],
  ['Ruy Lopez Classical', 'e4 e5 Nf3 Nc6 Bb5 Bc5'],
  ['Scandinavian Defense', 'e4 d5'],
  ['Scandinavian Modern', 'e4 d5 exd5 Nf6'],
  ['Scheveningen Variation', 'e4 c5 Nf3 d6 d4 cxd4 Nxd4 Nf6 Nc3 e6'],
  ['Schliemann Defense', 'e4 e5 Nf3 Nc6 Bb5 f5'],
  ['Sicilian Defense', 'e4 c5'],
  ['Sicilian Dragon', 'e4 c5 Nf3 d6 d4 cxd4 Nxd4 Nf6 Nc3 g6'],
  ['Sicilian Four Knights', 'e4 c5 Nf3 e6 d4 cxd4 Nxd4 Nf6 Nc3 Nc6'],
  ['St. George Defense', 'e4 a6'],
  ['Steinitz Defense', 'e4 e5 Nf3 Nc6 Bb5 d6'],
  ['Sveshnikov Variation', 'e4 c5 Nf3 Nc6 d4 cxd4 Nxd4 Nf6 Nc3 e5'],
  ['Taimanov Variation', 'e4 c5 Nf3 e6 d4 cxd4 Nxd4 Nc6'],
  ['Two Knights Defense', 'e4 e5 Nf3 Nc6 Bc4 Nf6'],
  // vs 1. d4
  ['Albin Countergambit', 'd4 d5 c4 e5'],
  ['Baltic Defense', 'd4 d5 c4 Bf5'],
  ['Benko Gambit', 'd4 Nf6 c4 c5 d5 b5'],
  ['Benoni Defense', 'd4 Nf6 c4 c5'],
  ['Bogo-Indian Defense', 'd4 Nf6 c4 e6 Nf3 Bb4+'],
  ['Budapest Gambit', 'd4 Nf6 c4 e5'],
  ['Chigorin Defense', 'd4 d5 c4 Nc6'],
  ['Czech Benoni', 'd4 Nf6 c4 c5 d5 e5'],
  ['Dutch Defense', 'd4 f5'],
  ['English Defense', 'd4 e6 c4 b6'],
  ['Englund Gambit', 'd4 e5'],
  ['Grünfeld Defense', 'd4 Nf6 c4 g6 Nc3 d5'],
  ['King’s Indian Defense', 'd4 Nf6 c4 g6'],
  ['Leningrad Dutch', 'd4 f5 g3 Nf6 Bg2 g6'],
  ['Modern Benoni', 'd4 Nf6 c4 c5 d5 e6'],
  ['Nimzo-Indian Defense', 'd4 Nf6 c4 e6 Nc3 Bb4'],
  ['Old Indian Defense', 'd4 Nf6 c4 d6'],
  ['Polish Defense', 'd4 b5'],
  ['Queen’s Gambit Accepted', 'd4 d5 c4 dxc4'],
  ['Queen’s Gambit Declined', 'd4 d5 c4 e6'],
  ['Queen’s Indian Defense', 'd4 Nf6 c4 e6 Nf3 b6'],
  ['Semi-Slav Defense', 'd4 d5 c4 c6 Nf3 Nf6 Nc3 e6'],
  ['Slav Defense', 'd4 d5 c4 c6'],
  ['Stonewall Dutch', 'd4 f5 c4 Nf6 g3 e6 Bg2 d5'],
  ['Tarrasch Defense', 'd4 d5 c4 e6 Nc3 c5'],
];

function build(side: OpeningSide, raw: Array<[string, string]>): Opening[] {
  return raw
    .map(([name, moves]) => define(side, name, moves))
    .sort((a, b) => a.name.localeCompare(b.name, 'en'));
}

export const WHITE_OPENINGS: Opening[] = build('white', WHITE_RAW);
export const BLACK_OPENINGS: Opening[] = build('black', BLACK_RAW);

export function openingSide(name: string): OpeningSide {
  return BLACK_OPENINGS.some((o) => o.name === name) ? 'black' : 'white';
}

export function findOpening(name: string): Opening | undefined {
  return (
    WHITE_OPENINGS.find((o) => o.name === name) ??
    BLACK_OPENINGS.find((o) => o.name === name)
  );
}

function moveTokens(moves: string): string[] {
  return moves.trim().split(/\s+/).filter(Boolean);
}

function isMovePrefix(prefix: string[], full: string[]): boolean {
  if (prefix.length >= full.length) return false;
  return prefix.every((san, i) => full[i] === san);
}

export function allOpenings(): Opening[] {
  return [...WHITE_OPENINGS, ...BLACK_OPENINGS];
}

export type OpeningMoveFilter = 'all' | '1' | '2' | '3' | '4' | 'more';

export function openingMoveCount(opening: Opening): number {
  return opening.moves.trim().split(/\s+/).filter(Boolean).length;
}

export function filterOpeningsByMoves(
  openings: readonly Opening[],
  filter: OpeningMoveFilter,
): Opening[] {
  if (filter === 'all') return [...openings];
  return openings.filter((opening) => {
    const moves = openingMoveCount(opening);
    return filter === 'more' ? moves > 4 : moves === Number(filter);
  });
}

/** Named lines that continue this move order. */
export function transpositionsInto(
  opening: Opening,
  catalog: readonly Opening[] = allOpenings(),
): Opening[] {
  const prefix = moveTokens(opening.moves);
  return catalog
    .filter((o) => o.name !== opening.name && isMovePrefix(prefix, moveTokens(o.moves)))
    .sort((a, b) => a.name.localeCompare(b.name, 'en'));
}

/** Named lines this move order continues from. */
export function transpositionsFrom(
  opening: Opening,
  catalog: readonly Opening[] = allOpenings(),
): Opening[] {
  const full = moveTokens(opening.moves);
  return catalog
    .filter((o) => o.name !== opening.name && isMovePrefix(moveTokens(o.moves), full))
    .sort((a, b) => a.name.localeCompare(b.name, 'en'));
}

/** Numbered SAN line, e.g. 1.e4 e5 2.Nf3 Nc6 3.Bc4 */
export function formatMoveLine(moves: string): string {
  const parts = moves.trim().split(/\s+/).filter(Boolean);
  const bits: string[] = [];
  for (let i = 0; i < parts.length; i++) {
    const san = parts[i]!;
    if (i % 2 === 0) bits.push(`${i / 2 + 1}.${san}`);
    else bits.push(san);
  }
  return bits.join(' ');
}

/** Case-insensitive name match. Empty query → no results. */
export function searchOpenings(
  openings: readonly Opening[],
  query: string,
): Opening[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  return openings
    .filter((o) => o.name.toLowerCase().includes(needle))
    .sort((a, b) => a.name.localeCompare(b.name, 'en'));
}

/** Group openings into e4 / d4 / other (each group sorted A–Z). */
export function groupOpenings(openings: Opening[]): OpeningGroup[] {
  return OPENING_GROUP_ORDER.map((id) => ({
    id,
    label: OPENING_GROUP_LABELS[id],
    openings: openings
      .filter((o) => o.group === id)
      .sort((a, b) => a.name.localeCompare(b.name, 'en')),
  })).filter((g) => g.openings.length > 0);
}
